import { useEffect, useState } from 'react'
import { ReadyState } from 'react-use-websocket'
import { SendJsonMessage } from 'react-use-websocket/dist/lib/types'
import { Content as ContentType } from '@phrasee/phrasee-typings'
import { useQueryClient } from '@tanstack/react-query'

import { errorToast } from 'common/components/toastNotification'
import { useAppDispatch } from 'common/hooks/redux'
import useWebsockets, { WsResponseBase } from 'common/hooks/useWebsockets'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'
import {
  selectElement,
  toggleElementsDisabled,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

import { getAllComponentElements } from '../helpers'

import useContent from './useContent'

interface WsResponse extends WsResponseBase {
  content?: ContentType
  elementId?: number
  errorMessage?: string
}

export const getGenerateWsTopic = (contentId: string, elementId: number) =>
  `generate/${contentId}/${elementId}`

const useWebsocketStatus = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const [nglPending, setNglPending] = useState(false)
  const {
    lastJsonMessage,
    sendJsonMessage,
    readyState,
  }: {
    lastJsonMessage: WsResponse
    sendJsonMessage: SendJsonMessage
    readyState: ReadyState
  } = useWebsockets()

  const { content } = useContent()

  useEffect(() => {
    if (content && lastJsonMessage) {
      const elementId = Number(lastJsonMessage.elementId)
      const wsTopic = getGenerateWsTopic(content._id, elementId)

      if (lastJsonMessage.topics?.includes(wsTopic)) {
        if (lastJsonMessage.status === 'success') {
          queryClient.setQueryData(
            contentKeys.content(content._id),
            lastJsonMessage.content
          )

          sendJsonMessage({
            action: 'unsubscribe',
            topics: [wsTopic],
          })

          dispatch(toggleElementsDisabled(false))
          dispatch(
            selectElement({
              elementId: elementId,
              shouldShowUnsavedModal: false,
            })
          )
        } else if (lastJsonMessage.status === 'error' && content) {
          queryClient.invalidateQueries(contentKeys.content(content._id))
          errorToast(lastJsonMessage.errorMessage)
          dispatch(toggleElementsDisabled(false))
        }
      }
    }
    // Respond only to lastJsonMessage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage])

  useEffect(() => {
    setNglPending(false)

    if (content && readyState === ReadyState.OPEN) {
      const allComponentElements = getAllComponentElements(content)
      allComponentElements.forEach((element) => {
        if (element.nlg_status === 'pending') {
          const wsTopic = getGenerateWsTopic(content._id, element.element_id)
          setNglPending(true)
          sendJsonMessage({
            action: 'subscribe',
            topics: [wsTopic],
          })
        }
      })
    }
  }, [content, readyState, sendJsonMessage])

  return {
    nglPending,
  }
}

export default useWebsocketStatus
